import {
  computed,
  InjectionKey, onMounted, onUnmounted, provide, reactive, ref, toRef, watch
} from 'vue';
import {last} from "radash";
import {ValidationSchema} from "fastest-validator";
import {Ref, UnwrapRef} from "@vue/reactivity";
import {WatchStopHandle} from "@vue/runtime-core";
import {useValidator} from "@/composable/ui/useValidator";

export type ValidatorRule<T> = (((value: T) => string | boolean)[]) | ValidationSchema;
export type RuleType = (...args: (unknown)[]) => string | boolean;
export type FormRules<T extends {[key: string]: any}> = {[key in keyof T]: T[key] extends {[key: string]: unknown} ? FormRules<T[key]> : RuleType[]};
export interface FormField<T = unknown> {
  value: Ref<T>;
  rules: Ref<ValidatorRule<T>>;
}
export interface FormFieldRef<T = unknown> extends FormField<T> {
  id: number;
  ignoreRequired?: boolean;
  error: Ref<UnwrapRef<string>>
  stopWatch: WatchStopHandle;
}

export interface FormProvider {
  registerInput: (item: FormField, dependencyValue?: Ref<string | number | undefined>, disableRequired?: boolean) => {id: number, error: Ref<unknown>};
  unregisterInput: (el: number) => void;
}
export const FormKey: InjectionKey<FormProvider> = Symbol('Form');

const forcedValidate = ref(false);
export default function useForm() {

  const isValid = ref(false);
  const fields: FormFieldRef[] = [];
  const validator = useValidator();
  function registerInput(formField: FormField, dependencyValue?: Ref<string | number | undefined>, disableRequired?: boolean): {id: number, error: Ref<string>} {
    const lastItem = last(fields);
    const dependencyValueRef = computed(() => dependencyValue?.value)
    const error = ref('');
    const newItem: FormFieldRef = {
      id: 1,
      rules: formField.rules,
      value: formField.value,
      error: error,
      ignoreRequired: disableRequired,
      stopWatch: watch([formField.value, dependencyValueRef],  () => {
        isValid.value = !fields.some((item) => !item.ignoreRequired && validator.validate(item.rules.value, item.value.value));
        error.value = validator.validate(formField.rules.value, formField.value.value);
      })
    };

    watch(forcedValidate, () => {
      if(!forcedValidate.value) return;
      isValid.value = !fields.some((item) => !item.ignoreRequired && validator.validate(item.rules.value, item.value.value));
      error.value = validator.validate(formField.rules.value, formField.value.value);
    })

    if(lastItem) newItem.id = lastItem.id + 1;
    fields.push(newItem);

    return {
      id: newItem.id,
      error: newItem.error,
    };
  }
  function unregisterInput(el: number) {
    const idx = fields.findIndex((item) => item.id === el);
    if(idx === -1) return;
    const item = fields[idx];
    item.stopWatch();
    fields.splice(idx, 1);
  }

  const formProvider: FormProvider = {
    registerInput,
    unregisterInput,
  }

  provide(FormKey, formProvider);

  onMounted(() => {
      isValid.value = !fields.some((item) => !item.ignoreRequired && validator.validate(item.rules.value, item.value.value));
  })

  return {
    isValid,
  }
}

export function useForceValidate(){
  function forceValidate() {
    forcedValidate.value = true;
    const elements = document.querySelectorAll('.invalidField');
    if(elements.length) {
      const firstElement = elements[0];
      if(firstElement instanceof HTMLElement) {
        firstElement.focus({ preventScroll: true });
        firstElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest', });
      }
    };
  }

  onUnmounted(() => {
    forcedValidate.value = false;
  })

  return {
    forceValidate,
  }
}
